import React from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'
import {Box, Heading} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Component, regionPropType} from '@salesforce/commerce-sdk-react/components'
import {BlackArrow} from '../../../components/icons'
import {pdProductCarousalStyles} from '../../../theme/components/project/pdpage-product-recommendation-styles'

/**
 * Display child components in a carousel slider manner. Configurations include the number of
 * children to display in view as well as whether or not to show controls and position indicators.
 *
 * @param {PageProps} props
 * @param {string} props.textHeading - Heading text for the productcarousel.
 * @param {boolean} props.xsCarouselIndicators - Show/Hide carousel indicators/pips on "xs" screens.
 * @param {boolean} props.smCarouselIndicators - Show/Hide carousel indicators/pips on "sm" screens.
 * @param {boolean} props.mdCarouselIndicators - Show/Hide carousel indicators/pips on "md" screens.
 * @param {boolean} props.xsCarouselControls - Show/Hide carousel forward/back controls on "xs" screens.
 * @param {boolean} props.smCarouselControls - Show/Hide carousel forward/back controls on "sm" screens.
 * @param {number} props.xsCarouselSlidesToDisplay - Number of children that will be rendered in view on "xs" screens.
 * @param {number} props.smCarouselSlidesToDisplay - Number of children that will be rendered in view on "sm" screens.
 * @param {number} props.mdCarouselSlidesToDisplay - Number of children that will be rendered in view on "md" screens.
 * @param {Object []} props.region - The regions passed internally to this component by the `commerce-sdk-react` Page component.
 * @returns {React.ReactElement} - Carousel component.
 */
export const ProductCarousel = (props = {}) => {
    const {
        textHeadline,
        isInfinite,
        isDisplayArrows,
        isAutoplay,
        xsCarouselIndicators,
        smCarouselIndicators,
        mdCarouselIndicators,
        xsCarouselControls,
        smCarouselControls,
        xsCarouselSlidesToDisplay,
        smCarouselSlidesToDisplay,
        mdCarouselSlidesToDisplay,
        regions
    } = props

    const components = regions[0]?.components || []

    const NextArrow = ({className, onClick}) => (
        <Box
            className={className}
            onClick={onClick}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
            position="absolute"
            right={'0'}
        >
            <BlackArrow />
        </Box>
    )

    const PrevArrow = ({className, onClick}) => (
        <Box
            className={className}
            onClick={onClick}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
            transform="rotate(180deg)"
            position="absolute"
            right="105px"
        >
            <BlackArrow />
        </Box>
    )

    const settings = {
        dots: mdCarouselIndicators,
        autoplay: isAutoplay,
        infinite: isInfinite,
        arrows: isDisplayArrows,
        speed: 500,
        slidesToShow: 5,
        slidesToScroll: 1,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    dots: mdCarouselIndicators,
                    slidesToShow: mdCarouselSlidesToDisplay,
                    slidesToScroll: 1
                    // infinite: true
                }
            },
            {
                breakpoint: 768,
                settings: {
                    dots: smCarouselIndicators,
                    slidesToShow: smCarouselSlidesToDisplay,
                    slidesToScroll: 1,
                    arrows: smCarouselControls
                }
            },
            {
                breakpoint: 640,
                settings: {
                    dots: xsCarouselIndicators,
                    slidesToShow: xsCarouselSlidesToDisplay,
                    slidesToScroll: 1,
                    arrows: xsCarouselControls
                }
            }
        ]
    }

    return (
        <Box
            className={'carousel carousel-wrapper product-wrapper'}
            data-testid="carousel"
            sx={pdProductCarousalStyles}
        >
            <Box className="pd-product-carousal-container">
                <Heading className="recommenderHeading" as={'h2'}>
                    {textHeadline ?? ''}
                </Heading>

                <Slider {...settings} className="carousel-sg">
                    {components.map((component, index) => (
                        <Component key={component?.id || index} component={component} />
                    ))}
                </Slider>
            </Box>
        </Box>
    )
}

ProductCarousel.propTypes = {
    regions: PropTypes.arrayOf(regionPropType).isRequired,
    textHeadline: PropTypes.string,
    isInfinite: PropTypes.bool,
    isDisplayArrows: PropTypes.bool,
    isAutoplay: PropTypes.bool,
    xsCarouselIndicators: PropTypes.bool,
    smCarouselIndicators: PropTypes.bool,
    mdCarouselIndicators: PropTypes.bool,
    xsCarouselControls: PropTypes.bool,
    smCarouselControls: PropTypes.bool,
    xsCarouselSlidesToDisplay: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    smCarouselSlidesToDisplay: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    mdCarouselSlidesToDisplay: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    className: PropTypes.string,
    onClick: PropTypes.func
}

ProductCarousel.displayName = 'ProductCarousel'

export default ProductCarousel
